import Vue from 'vue';
import Vuex from 'vuex';
import modules from './modules';
import persistStore from './persistence';
import api from '../helper/api';

Vue.use(Vuex);

const store = new Vuex.Store({
	state: {
		offline: false,
		loadPwa: 0,
		donwloadedPwa: true,
		options: {},
		firstLoad: true,
		firstLoadIOS: true,
	},
	mutations: {
		setOffline(state, value) {
			state.offline = value;
		},
		setLoadPwa(state, value) {
			state.loadPwa = value;
		},
		setFirstLoad(state, value) {
			state.firstLoad = value;
		},
		setFirstLoadIOS(state, value) {
			state.firstLoadIOS = value;
		},
		setDownloadPwa(state, value) {
			state.donwloadedPwa = value;
		},
		setOptions(state, value) {
			state.options = value;
		}
	},
	actions: {
		loadingPwa({ commit }, value) {
			commit('setLoadPwa', value);
			if (value == 2) {
				setTimeout(function () {
					commit('setDownloadPwa', false);
					commit('setLoadPwa', 0);
				}, 1500);
			}
		},
		setFirstLoad({ commit }, value) {
			commit('setFirstLoad', value);
		},
		setFirstLoadIOS({ commit }, value) {
			commit('setFirstLoadIOS', value);
		},
		/**
		 * Sets the offline flag to true.
		 * 
		 * @param {Object} context
		 */
		offline({ commit }) {
			commit('setOffline', true);
		},
		/**
		 * Sets the offline flag to false.
		 * Also runs deferred tasks and reloads user data.
		 * 
		 * @param {Object} context
		 */
		async online({ commit, dispatch }) {
			commit('setOffline', false);

			// Ensure that the store got restored from the persistent storage (The restored promise is provided by the vuex-persistent plugin)
			await store.restored;

			// Run the tasks which got deferred while we have been offline
			dispatch('tasks/runStack');

			if (store.getters['user/loggedIn']) {
				// Update the user data from the api if the user is logged in
				dispatch('user/updateFromSource');
			}
		},
		/**
		 * Checks the internet connection by fetching something from the api.
		 * Dispatches either the 'online' or the 'offline' action.
		 * 
		 * @param {Object} context 
		 * @returns {Promise} The promise of the connection check api fetch.
		 */
		checkConnection({ dispatch, getters }) {
			// if logged in, check the connection by trying to reload the options, otherwise just fetch the 'check' api endpoint (which does not respond with any sensitive app data)
			const connectionCheck = getters['user/loggedIn'] ? dispatch('loadOptions') : api.get('check').run();

			connectionCheck.then(() => {
				dispatch('online');
			}).catch(() => {
				dispatch('offline');
			});

			return connectionCheck;
		},
		/**
		 * Loads the app options from the api.
		 * Commits 'setOptions' after a successfull api fetch.
		 * 
		 * @param {Object} context
		 * @returns {Promise} The promise of the api fetch.
		 */
		loadOptions({ commit }) {
			const loadOptions = api.get('options').run();

			loadOptions.then(response => {
				commit('setOptions', response.valueOf());
			});

			return loadOptions;
		},
	},
	modules: Object.fromEntries(Object.entries(modules).map(([key, value]) => { return [key, value.module]; })),
	getters: {
		isFirstLoad: state => {
			return state.firstLoad;
		},
		isFirstLoadIOS: state => {
			return state.firstLoadIOS;
		},
	}
});

store.on = {
	saved: {
		$storeSavedPromise: {}
	}
};

/**
 * Returns a new promise which gets resolved after a save operation on the store is completed.
 * Filters the save operations by store module, mutation and payload if given.
 * 
 * @param {String} [module] Optional store module filter
 * @param {String} [mutation] Optional store mutation name filter
 * @param {*} [payload] Optional store mutation payload filter (if the payload is an object every given payload value has to be in the store mutation payload with the same key for a match)
 */
store.saved = (module = undefined, mutation = undefined, payload = undefined) => {
	return new Promise((resolve) => {
		const eventHandlerKeys = Object.keys(store.on.saved.$storeSavedPromise);
		const eventHandlerIndex = eventHandlerKeys.length == 0 ? 0 : Math.max(...eventHandlerKeys) + 1;
		const payloadEntries = payload && typeof payload == 'object' ? Object.entries(payload) : null;

		store.on.saved.$storeSavedPromise[eventHandlerIndex] = trigger => {
			if ((!module || trigger.module == module) && (!mutation || trigger.mutation == mutation)) {
				if (!payload || (trigger.payload && (payload == trigger.payload || (payloadEntries && typeof trigger.payload == 'object' && payloadEntries.every(([k, v]) => k in trigger.payload && trigger.payload[k] == v))))) {
					delete store.on.saved.$storeSavedPromise[eventHandlerIndex];
					resolve(trigger);
				}
			}
		};
	});
};

// Enable persistent storing for our store
persistStore(store, modules, trigger => {
	Object.values(store.on.saved).forEach(onSavedHandler => {
		if (typeof onSavedHandler != 'function') {
			if (typeof onSavedHandler == 'object' && !Array.isArray(onSavedHandler)) {
				onSavedHandler = Object.values(onSavedHandler);
			}

			if (Array.isArray(onSavedHandler)) {
				for (const handler of onSavedHandler) {
					handler(trigger);
				}
			} else {
				throw new Error('Invalid store on save handler given');
			}
		} else {
			onSavedHandler(trigger);
		}
	})
});

export default store;